<template>
  <div>
    <SchemaTablePage
      ref="tablePageRef"
      layout-mode="page"
      :search-schema="searchSchema"
      :columns="tableColumns"
      :default-query="defaultQuery"
      :request="request"
      :build-params="buildParams"
      :parse-result="parseResult"
      :table-attrs="tableAttrs"
    >
      <template #table-header>
        <div class="actions">
          <el-button type="primary" size="mini" @click="handleAdd">新增</el-button>
          <el-button size="mini" @click="handleSpecialSetting">特殊公告设置</el-button>
        </div>
      </template>

      <template #colTitle="{ row }">
        <LanguageInputEcho :obj="row.title || {}" />
      </template>
      <template #colStatus="{ row }">
        <el-tag :type="getStatusTagType(row.status)" size="small">
          {{ getStatusText(row.status) }}
        </el-tag>
      </template>
      <template #colType="{ row }">{{ getTypeText(row.type) }}</template>
      <template #colCreatedAt="{ row }">{{ row.created_at ? parseTime(row.created_at) : '--' }}</template>
      <template #colPublishAt="{ row }">{{ row.publish_at ? parseTime(row.publish_at) : '--' }}</template>
      <template #colActions="{ row }">
        <el-button type="text" size="mini" @click="handleEdit(row)">编辑</el-button>

        <el-tooltip
          v-if="row.disable_at !== 0 && row.disable_at < new Date().getTime()"
          placement="top"
          content="已过期，请修改停用时间后再启用"
        >
          <el-button type="text" size="mini" disabled>启用</el-button>
        </el-tooltip>

        <el-button v-else type="text" size="mini" @click="handleToggleStatus(row)">
          {{ row.status === 1 ? '停用' : '启用' }}
        </el-button>

        <el-popconfirm
          v-if="row.status === 2"
          title="确定删除吗？"
          confirm-button-text="确定"
          cancel-button-text="取消"
          type="warning"
          @confirm="handleDelete(row)"
        >
          <el-button slot="reference" type="text" class="danger-button" size="mini" style="margin-left: 8px"
            >删除</el-button
          >
        </el-popconfirm>
      </template>
    </SchemaTablePage>

    <ManageSystemNoticeDialog
      :visible.sync="noticeDialogVisible"
      :dialog-title="noticeDialogTitle"
      :form-data="currentNoticeForm"
      @success="handleDialogSuccess"
    />

    <SpecialNoticeSettingDialog :visible.sync="specialDialogVisible" @success="handleSpecialDialogSuccess" />
  </div>
</template>

<script>
import SchemaTablePage from '@/components/SchemaTablePage'
import { deleteAnnouncement, getAnnouncementList, updateAnnouncementStatus } from '@/api/content/announcement'
import LanguageInputEcho from '@/components/LanguageInputEcho'
import { parseTime } from '@/utils'
import ManageSystemNoticeDialog from './components/ManageSystemNoticeDialog.vue'
import SpecialNoticeSettingDialog from './components/SpecialNoticeSettingDialog.vue'

const defaultQuery = {
  title: '',
  status: '',
  type: '',
  dateRange: [],
}

export default {
  name: 'SystemNotice',
  components: {
    SchemaTablePage,
    ManageSystemNoticeDialog,
    SpecialNoticeSettingDialog,
    LanguageInputEcho,
  },
  data() {
    return {
      tableAttrs: {
        size: 'mini',
        highlightCurrentRow: true,
      },
      tableColumns: [
        { type: 'index', label: '序号', width: 60, align: 'center', resizable: false },
        { prop: 'title', label: '标题', minWidth: 300, slot: 'colTitle', align: 'left' },
        { prop: 'status', label: '状态', width: 80, align: 'center', resizable: false, slot: 'colStatus' },
        { prop: 'type', label: '类型', width: 80, align: 'center', resizable: false, slot: 'colType' },
        { prop: 'created_at', label: '创建时间', minWidth: 160, resizable: false, slot: 'colCreatedAt' },
        { prop: 'publish_at', label: '发布时间', minWidth: 160, resizable: false, slot: 'colPublishAt' },
        { prop: 'operator', label: '操作人', minWidth: 100, resizable: false, align: 'center' },
        { label: '操作', width: 120, fixed: 'right', align: 'left', resizable: false, slot: 'colActions' },
      ],
      searchSchema: [
        {
          prop: 'title',
          label: '标题',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'status',
          label: '状态',
          component: 'select',
          componentProps: { clearable: true },
          options: [
            { label: '全部', value: '' },
            { label: '启用', value: 1 },
            { label: '停用', value: 2 },
          ],
        },
        {
          prop: 'type',
          label: '类型',
          component: 'select',
          componentProps: { clearable: true },
          options: [
            { label: '全部', value: '' },
            { label: '普通', value: 1 },
            { label: '特殊', value: 2 },
          ],
        },
        {
          prop: 'dateRange',
          label: '创建时间',
          component: 'datetime-range',
          componentProps: { defaultTime: ['00:00:00', '23:59:59'] },
        },
        { component: 'actions' },
      ],
      defaultQuery: { ...defaultQuery },
      noticeDialogVisible: false,
      noticeDialogTitle: '新增系统公告',
      currentNoticeForm: {},
      specialDialogVisible: false,
    }
  },
  methods: {
    parseTime,
    request(params) {
      return getAnnouncementList(params)
    },
    buildParams({ query, page, limit }) {
      const params = {
        page,
        page_size: limit,
      }
      if (query.title) params.title = query.title
      if (query.status !== '') params.status = query.status
      if (query.type !== '') params.type = query.type
      if (query.dateRange && query.dateRange.length === 2) {
        params.start_at = Math.floor(new Date(query.dateRange[0]).getTime())
        params.end_at = Math.floor(new Date(query.dateRange[1]).getTime())
      }
      return params
    },
    parseResult(response) {
      if (response && response.code === 0) {
        return {
          list: (response.data && response.data.list) || [],
          total: (response.data && response.data.total) || 0,
        }
      }
      this.$message.error(response?.msg || '获取公告列表失败')
      return { list: [], total: 0 }
    },

    getStatusText(status) {
      const map = {
        1: '启用',
        2: '停用',
      }
      return map[status] || '--'
    },

    getStatusTagType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'info'
      return 'default'
    },

    getTypeText(type) {
      const map = {
        1: '普通',
        2: '特殊',
      }
      return map[type] || '--'
    },

    handleAdd() {
      this.noticeDialogTitle = '新增系统公告'
      this.currentNoticeForm = {
        type: 1,
      }
      this.noticeDialogVisible = true
    },

    handleEdit(row) {
      this.noticeDialogTitle = '编辑系统公告'
      this.currentNoticeForm = {
        ...row,
        enable_at: row.enable_at || 0,
        disable_at: row.disable_at || 0,
      }
      this.noticeDialogVisible = true
    },

    async handleToggleStatus(row) {
      const targetStatus = row.status === 1 ? 2 : 1
      const actionText = targetStatus === 2 ? '停用' : '启用'

      try {
        await this.$confirm(`确定要${actionText}这条公告吗？`, `${actionText}公告`, {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        })
      } catch (e) {
        return
      }

      try {
        const resp = await updateAnnouncementStatus({ id: row.id, status: targetStatus })
        if (resp && resp.code === 0) {
          this.$message.success(`${actionText}成功`)
          this.$refs.tablePageRef?.reload?.()
        } else if (resp) {
          this.$message.error(resp.msg || `${actionText}失败`)
        }
      } catch (e) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    },

    handleSpecialSetting() {
      this.specialDialogVisible = true
    },

    handleDialogSuccess() {
      this.$refs.tablePageRef?.reload?.()
    },

    handleSpecialDialogSuccess() {
      this.$message.success('特殊公告设置已保存')
    },

    async handleDelete(row) {
      try {
        await deleteAnnouncement({ id: row.id })
        this.$message.success('删除成功')
        this.$refs.tablePageRef?.reload?.()
      } catch (e) {
        this.$message.error(e.message || '删除失败')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.actions {
  display: flex;
  align-items: center;

  .special-link {
    font-size: 13px;
  }
}

.ellipsis-text {
  cursor: pointer;
}

.rich-preview {
  max-width: 200px;
  max-height: 320px;
  overflow: auto;
}

::v-deep .rich-preview img {
  max-width: 200px;
  height: auto;
}

::v-deep .content-cell img {
  max-width: 50px;
  max-height: 50px;
  object-fit: cover;
  display: inline-block;
}
</style>
